import React from 'react';
import { useIntl } from 'react-intl';
import { Form, Input } from 'semantic-ui-react';
import {
  firstError, firstResponseError, checkRequired, checkMaxLength, validationClasses,
} from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import fields, { fieldLengths, globalFieldLengths } from '@/constants/fields';
import MarkdownEditor from '@/components/text-editors/MarkdownEditor';
import FormValidationError from '@/components/errors/FormValidationError';
import { datasetSubscriptions } from '@/notifications/subscribers/constants';
import Dialog from '@/components/dialogs/Dialog';
import Selectors from '../hooks/selectors';
import Events from '../hooks/events';

const DatasetNotificationCreateDialog = ({ theme }) => {
  const intl = useIntl();
  const state = Selectors.useDatasetNotificationCreateDialog();
  const hide = Events.useHideDatasetNotificationCreate();
  const postData = Events.useCreateDatasetNotification(datasetSubscriptions.DATASET_UPDATE);
  const updateProperties = Events.useUpdateDatasetNotificationCreateProperties();

  const validateForm = () => {
    const errors = [];
    const { name, description } = state;
    const entity = 'dataset.notify';
    const nameTitle = 'save.dataset.notify.validation.name.title';
    const descTitle = 'save.dataset.notify.validation.description.title';

    checkRequired(name, [fields.NAME], errors, entity, nameTitle, true, 'validation.name.required');
    checkMaxLength(name, [fields.NAME], errors, entity, nameTitle, globalFieldLengths.name);
    checkRequired(
      description,
      [fields.DESCRIPTION],
      errors,
      entity,
      descTitle,
      true,
      'validation.description.required',
    );
    checkMaxLength(description, [fields.DESCRIPTION], errors, entity, descTitle, fieldLengths.huge);

    return errors;
  };

  const submitForm = () => {
    const createErrors = validateForm();
    updateProperties({ properties: { createErrors } });

    if (createErrors.length === 0) {
      postData();
    }
  };

  const nameError = state.createErrors.find((e) => (e.fields.includes(fields.NAME)));
  const descriptionError = state.createErrors.find((e) => (e.fields.includes(fields.DESCRIPTION)));

  return (
    <Dialog
      className="aridhia-dataset-notify-dialog"
      error={firstResponseError(state.createErrors)}
      icon="send"
      inProgress={state.createStatus === states.PENDING}
      onClose={hide}
      onSave={submitForm}
      message="dataset.notify.message.content"
      minimumDataEntryMet={state.name && state.description}
      saveMessageId="actions.send"
      size="small"
      theme={theme}
      title={intl.formatMessage({ id: 'dataset.notify.title' }, { name: state.datasetName })}
    >
      <Form autoComplete="off" onSubmit={submitForm} error={firstError(state.createErrors) !== null}>
        <FormValidationError
          error={nameError}
          htmlFor="dataset_notify_name"
          labelId="save.dataset.notify.name"
          position="right"
        >
          <Input
            id="dataset_notify_name"
            autoFocus
            disabled={state.createStatus === states.PENDING}
            type="custom"
            size="large"
            placeholder={intl.formatMessage({ id: 'save.dataset.notify.name.placeholder' })}
            value={state.name}
            maxLength={globalFieldLengths.name}
            onChange={(e) => updateProperties({ properties: { name: e.target.value } })}
            className={validationClasses(nameError)}
          />
        </FormValidationError>
        <FormValidationError
          error={descriptionError}
          htmlFor="dataset_notify_description"
          labelId="save.dataset.notify.description"
        >
          <MarkdownEditor
            id="dataset_notify_description"
            disabled={state.createStatus === states.PENDING}
            value={state.description}
            onChange={(value) => updateProperties({ properties: { description: value } })}
            maxEditorHeight={240}
            minPreviewHeight={200}
            className={validationClasses(descriptionError)}
          />
        </FormValidationError>
      </Form>
    </Dialog>
  );
};

export default DatasetNotificationCreateDialog;
